"use client";

import { useEffect, useState } from "react";
import { projectsApi } from '@/lib/api';
import { CREATE_PLACES, CREATE_PLACE_GROUPS } from '@/lib/business-projects';
import { NeonCard } from "@/components/ui/neon-card";
import { CyberpunkProgress } from "@/components/ui/cyberpunk-progress";
import Link from "next/link";
import {
  ArrowRight,
  Clock,
  Target,
  FolderKanban,
  Zap,
  Pause,
  CheckCircle2,
  Filter,
  Loader2,
  Plus,
  X,
} from "lucide-react";

interface Project {
  id: number;
  slug: string;
  name: string;
  category: string | null;
  status: string;
  icon: string | null;
  goal: string | null;
  progress: number | null;
  estimatedMins: number | null;
  lastActiveAt: string | null;
}

const categoryConfig: Record<string, { color: string; glow: "cyan" | "magenta" | "green" | "yellow" | "none" }> = {
  wtf: { color: "text-cyan-400 bg-cyan-500/10", glow: "cyan" },
  ventures: { color: "text-green-400 bg-green-500/10", glow: "green" },
  amigo: { color: "text-blue-400 bg-blue-500/10", glow: "cyan" },
  homyak: { color: "text-teal-400 bg-teal-500/10", glow: "green" },
  ivan: { color: "text-indigo-400 bg-indigo-500/10", glow: "cyan" },
  brian: { color: "text-pink-400 bg-pink-500/10", glow: "magenta" },
  vsl: { color: "text-orange-400 bg-orange-500/10", glow: "yellow" },
  tools: { color: "text-purple-400 bg-purple-500/10", glow: "cyan" },
  family: { color: "text-yellow-400 bg-yellow-500/10", glow: "yellow" },
  other: { color: "text-gray-400 bg-gray-500/10", glow: "none" },
};

const statusConfig: Record<string, { icon: typeof Zap; color: string }> = {
  active: { icon: Zap, color: "text-green-400" },
  planning: { icon: Target, color: "text-cyan-400" },
  paused: { icon: Pause, color: "text-yellow-400" },
  done: { icon: CheckCircle2, color: "text-green-400" },
  archived: { icon: FolderKanban, color: "text-gray-500" },
};

function formatTime(mins: number | null): string {
  if (!mins) return "--";
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  const remaining = mins % 60;
  return remaining > 0 ? `${hours}h ${remaining}m` : `${hours}h`;
}

export default function ProjectsPage() {
  const [allProjects, setAllProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newProject, setNewProject] = useState({ name: "", place: "wtf", description: "", goal: "", icon: "" });

  const refreshProjects = () => {
    projectsApi.list()
      .then((data: any) => {
        setAllProjects(data.projects || []);
        setLoading(false);
      })
      .catch((err: any) => {
        console.error("Failed to load projects:", err);
        setLoading(false);
      });
  };

  useEffect(() => { refreshProjects(); }, []);

  // Deep-link: /projects?create=1&collab=collab-ivan opens the dialog prefilled
  // (entry point: ➕ New Project button on /p/collab-* headers).
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    if (q.get('create') === '1') {
      const collab = q.get('collab') || '';
      setNewProject(p => ({ ...p, place: CREATE_PLACES.some(c => c.key === collab) ? collab : p.place }));
      setShowCreate(true);
    }
  }, []);

  const createProject = async () => {
    if (!newProject.name.trim()) return;
    setCreating(true);
    try {
      const place = CREATE_PLACES.find((p) => p.key === newProject.place)!;
      // HTTP route always (NOT projectsApi.create — in Electron that switches to
      // the legacy IPC handler which skips real-tree paths + collab routing).
      const r = await fetch('/api/projects', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newProject.name, category: place.category, collab: place.collab,
          description: newProject.description, goal: newProject.goal, icon: newProject.icon,
        }),
      });
      const data = await r.json();
      if (data.ok) {
        setShowCreate(false);
        setNewProject({ name: "", place: "wtf", description: "", goal: "", icon: "" });
        window.location.href = `/projects/${data.project.slug}`;
      }
    } catch (e) {
      console.error("Failed to create project:", e);
    } finally {
      setCreating(false);
    }
  };

  // Auto-refresh every 10s when tab is visible
  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;
    const start = () => { interval = setInterval(refreshProjects, 10000); };
    const stop = () => { clearInterval(interval); };
    if (document.visibilityState === "visible") start();
    const handler = () => {
      if (document.visibilityState === "visible") start();
      else stop();
    };
    document.addEventListener("visibilitychange", handler);
    return () => { stop(); document.removeEventListener("visibilitychange", handler); };
  }, []);


  // Group by category
  const byCategory = allProjects.reduce((acc, p) => {
    const cat = p.category || "other";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(p);
    return acc;
  }, {} as Record<string, Project[]>);

  // Filter projects
  const filteredProjects = selectedCategory
    ? allProjects.filter((p) => (p.category || "other") === selectedCategory)
    : allProjects;

  if (loading) {
    return (
      <div className="min-h-screen" role="main" aria-label="Projects">
        <div className="p-6 space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-20 rounded-xl bg-[rgb(var(--muted))]/40 animate-pulse" />
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-48 rounded-xl bg-[rgb(var(--muted))]/40 animate-pulse" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" role="main" aria-label="Projects">

      {/* Create Project Dialog */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="Create new project" onClick={() => setShowCreate(false)}>
          <div className="bg-gray-900 border border-gray-700 rounded-2xl p-6 w-full max-w-md space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-white">New Project</h2>
              <button onClick={() => setShowCreate(false)} aria-label="Close dialog" className="text-gray-500 hover:text-gray-300"><X className="w-5 h-5" /></button>
            </div>
            <div>
              <label className="text-xs text-gray-400 uppercase tracking-wider">Name</label>
              <input
                type="text"
                value={newProject.name}
                onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                placeholder="My Awesome Project"
                className="w-full mt-1 px-3 py-2 bg-white/[0.06] border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-cyan-500"
                autoFocus
              />
            </div>
            <div>
              <label className="text-xs text-gray-400 uppercase tracking-wider">Where</label>
              <select
                value={newProject.place}
                onChange={(e) => setNewProject({ ...newProject, place: e.target.value })}
                className="w-full mt-1 px-3 py-2 bg-white/[0.06] border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-cyan-500"
              >
                {CREATE_PLACE_GROUPS.map((g) => (
                  <optgroup key={g} label={g}>
                    {CREATE_PLACES.filter((p) => p.group === g).map((p) => (
                      <option key={p.key} value={p.key}>{g === 'Collabs' ? `Collabs → ${p.label}` : p.label}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-400 uppercase tracking-wider">Icon</label>
              <div className="mt-1 grid grid-cols-10 gap-1.5">
                {["🚀", "💼", "🌐", "🛠️", "📱", "🎨", "🔒", "📊", "🤖", "💡",
                  "🏠", "🎯", "⚡", "🔥", "💎", "🌟", "📦", "🧪", "🎮", "📝",
                  "🏗️", "🛒", "💬", "📸", "🎵", "🍕", "🧠", "🌍", "🔮", "✨"].map((emoji) => (
                  <button
                    key={emoji}
                    type="button"
                    onClick={() => setNewProject({ ...newProject, icon: emoji })}
                    className={`w-8 h-8 flex items-center justify-center rounded-lg text-lg transition-all ${
                      newProject.icon === emoji
                        ? "bg-cyan-500/30 ring-2 ring-cyan-500 scale-110"
                        : "bg-[rgb(var(--muted))]/60 hover:bg-gray-700/60 hover:scale-105"
                    }`}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
              <div className="mt-2 flex items-center gap-2">
                <input
                  type="text"
                  value={newProject.icon}
                  onChange={(e) => setNewProject({ ...newProject, icon: e.target.value })}
                  placeholder="or type/paste any emoji"
                  className="flex-1 px-3 py-1.5 bg-white/[0.06] border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-cyan-500"
                  maxLength={4}
                />
                {newProject.icon && (
                  <span className="text-2xl">{newProject.icon}</span>
                )}
              </div>
            </div>
            <div>
              <label className="text-xs text-gray-400 uppercase tracking-wider">Goal</label>
              <textarea
                value={newProject.goal}
                onChange={(e) => setNewProject({ ...newProject, goal: e.target.value })}
                placeholder="What are you building and why?"
                rows={2}
                className="w-full mt-1 px-3 py-2 bg-white/[0.06] border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-cyan-500 resize-none"
              />
            </div>
            <div>
              <label className="text-xs text-gray-400 uppercase tracking-wider">Description (optional)</label>
              <textarea
                value={newProject.description}
                onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                placeholder="Brief description..."
                rows={2}
                className="w-full mt-1 px-3 py-2 bg-white/[0.06] border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-cyan-500 resize-none"
              />
            </div>
            <button
              onClick={createProject}
              disabled={!newProject.name.trim() || creating}
              className="w-full py-2.5 bg-cyan-500/20 border border-cyan-500/30 text-cyan-400 rounded-lg text-sm font-medium hover:bg-cyan-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {creating ? "Creating..." : "Create Project"}
            </button>
          </div>
        </div>
      )}

      <div className="p-6 space-y-6">
        {/* Header Row */}
        <div className="flex items-center justify-between">
          <div />
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-2 px-4 py-2 bg-cyan-500/20 border border-cyan-500/30 text-cyan-400 rounded-lg text-sm font-medium hover:bg-cyan-500/30 transition-colors"
          >
            <Plus className="w-4 h-4" />
            New Project
          </button>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 overflow-x-auto pb-2" role="tablist" aria-label="Filter by category">
          <button
            onClick={() => setSelectedCategory(null)}
            role="tab"
            aria-selected={selectedCategory === null}
            className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
              selectedCategory === null
                ? "bg-cyan-500/30 text-cyan-300 ring-1 ring-cyan-500/50"
                : "bg-cyan-500/10 text-cyan-400 hover:bg-cyan-500/20"
            }`}
          >
            All ({allProjects.length})
          </button>
          {Object.entries(byCategory).sort(([a], [b]) => {
            const order = ['wtf', 'ventures', 'brian', 'amigo', 'homyak', 'ivan', 'vsl', 'tools', 'family'];
            return (order.indexOf(a) === -1 ? 99 : order.indexOf(a)) - (order.indexOf(b) === -1 ? 99 : order.indexOf(b));
          }).map(([cat, projs]) => {
            const config = categoryConfig[cat] || categoryConfig.other;
            const isSelected = selectedCategory === cat;
            return (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                role="tab"
                aria-selected={isSelected}
                className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                  isSelected
                    ? `${config.color} ring-1 ring-current opacity-100`
                    : `${config.color} opacity-70 hover:opacity-100`
                }`}
              >
                {cat} ({projs.length})
              </button>
            );
          })}
        </div>

        {/* Compact cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
          {filteredProjects.map((project) => {
            const catConfig = categoryConfig[project.category || "other"] || categoryConfig.other;
            const statConfig = statusConfig[project.status] || statusConfig.active;
            const pct = project.progress || 0;
            // 'LIVE' must mean live ON THE INTERNET, not 'complete and pushed'.
            // `|| pct >= 100` conflated the two: progress reaches 100 when a repo
            // is finished and pushed to GitHub, which says nothing about whether
            // a domain serves the site. roofix.ca showed LIVE while serving
            // "RooFix.ca for sale | Spaceship.com — $7,450". (2026-08-20)
            const live = (project as any).live === true;
            const parked = (project as any).parked === true;

            return (
              <Link
                key={project.id}
                href={`/projects/${project.slug}`}
                className="group flex flex-col gap-2 p-3 rounded-xl border border-white/[0.05] bg-white/[0.02] hover:bg-white/[0.05] hover:border-cyan-500/20 transition-colors"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-xl flex-shrink-0">{project.icon || "📁"}</span>
                  <span className="font-medium text-sm truncate group-hover:text-cyan-300 transition-colors">{project.name}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${catConfig.color}`}>{project.category || "other"}</span>
                  {live ? (
                    <span className="text-[10px] px-1.5 py-0.5 rounded text-emerald-400 bg-emerald-500/10 font-semibold">LIVE</span>
                  ) : parked ? (
                    <span title="domain answers, but serves a parking / for-sale page"
                      className="text-[10px] px-1.5 py-0.5 rounded text-amber-400 bg-amber-500/10 font-semibold">PARKED</span>
                  ) : (
                    <span className={`text-[10px] ${statConfig.color}`}>{project.status}</span>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-auto">
                  <div className="flex-1 h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
                    <div
                      className={`h-full rounded-full ${pct >= 100 ? 'bg-emerald-400/80' : pct >= 60 ? 'bg-cyan-400/80' : 'bg-yellow-400/70'}`}
                      style={{ width: `${Math.min(pct, 100)}%` }}
                    />
                  </div>
                  <span className="text-[11px] text-gray-500 tabular-nums flex-shrink-0">{pct}%</span>
                </div>
              </Link>
            );
          })}
        </div>

        {filteredProjects.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <FolderKanban className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No projects in this category</p>
          </div>
        )}
      </div>
    </div>
  );
}
